import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { toPersianDigits, parsePersianNumber } from '../i18n';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Paper,
  Divider,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  CloseRounded as CloseRoundedIcon,
  CompareArrowsRounded as CompareArrowsRoundedIcon,
} from '@mui/icons-material';
import FlagIcon from './FlagIcon';

const ConverterModal = ({ open, onClose, currencies, initialCurrency, t }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Base list of options, adding Toman/IRR as a standard target/source
  const allCurrencyOptions = useMemo(() => {
    const list = [...currencies];
    // Check if IRR/Toman exists, if not add it
    const hasIrr = list.some((c) => c.short === 'TOMAN' || c.short === 'IRR');
    if (!hasIrr) {
      list.push({
        short: 'TOMAN',
        full: t ? t('tomanName') : 'Iranian Toman',
        sell: '1',
        buy: '1',
        flag: '🇮🇷',
        type: 'currency',
      });
    }
    return list;
  }, [currencies, t]);

  const [fromShort, setFromShort] = useState(initialCurrency || currencies[0]?.short || 'USD');
  const [toShort, setToShort] = useState('TOMAN');
  const [amount, setAmount] = useState('1');
  const [rotation, setRotation] = useState(0);

  // Synchronize initial currency strictly when dialog opens
  useEffect(() => {
    if (open && initialCurrency) {
      setFromShort(initialCurrency);
    }
  }, [open, initialCurrency]);

  // Helper to translate short code (e.g. TOMAN -> تومان) for display
  const getDisplayShortCode = useCallback((shortCode) => {
    if (!shortCode) return '';
    if (shortCode === 'TOMAN' || shortCode === 'IRR') {
      return t ? t('toman') : 'TOMAN';
    }
    return shortCode;
  }, [t]);

  // Helper to extract numeric price in Toman/IRR
  const getNumericPrice = useCallback((shortCode) => {
    if (shortCode === 'TOMAN' || shortCode === 'IRR') return 1;
    const found = currencies.find((c) => c.short === shortCode);
    if (!found || !found.sell) return 1;
    const val = parsePersianNumber(found.sell);
    return val <= 0 ? 1 : val;
  }, [currencies]);

  // Perform cross conversion
  const fromPrice = useMemo(() => getNumericPrice(fromShort), [fromShort, getNumericPrice]);
  const toPrice = useMemo(() => getNumericPrice(toShort), [toShort, getNumericPrice]);

  // Unit rate: 1 FROM = X TO
  const unitRate = useMemo(() => {
    if (toPrice === 0) return 0;
    return fromPrice / toPrice;
  }, [fromPrice, toPrice]);

  // Total amount in TO currency
  const convertedTotal = useMemo(() => {
    const numAmount = parsePersianNumber(amount) || 0;
    const total = numAmount * unitRate;
    if (total >= 1000) {
      return total.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
    return total.toLocaleString(undefined, { maximumFractionDigits: 4 });
  }, [amount, unitRate]);

  // Swap From and To currencies
  const handleSwap = () => {
    setRotation((prev) => prev + 180);
    const temp = fromShort;
    setFromShort(toShort);
    setToShort(temp);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          backgroundColor: isDark ? '#141C2C' : '#FFFFFF',
          backgroundImage: 'none',
          p: 0.5,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800 }}>
          {t ? t('currencyConverter') : 'Currency Converter'}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {/* Amount Input */}
          <TextField
            label={t ? t('amountToConvert') : 'Amount to Convert'}
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            fullWidth
            inputProps={{ min: 0, step: 'any' }}
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
            }}
          />

          {/* Input 1: From Currency Selector */}
          <TextField
            select
            label={t ? t('fromCurrency') : 'From Currency'}
            value={fromShort}
            onChange={(e) => setFromShort(e.target.value)}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
            }}
          >
            {allCurrencyOptions.map((item, idx) => (
              <MenuItem key={idx} value={item.short}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <FlagIcon short={item.short} flag={item.flag} size={22} />
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {item.full || item.short}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ marginInlineStart: 'auto' }}>
                    {getDisplayShortCode(item.short)}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          {/* Swap Button (Swaps Position of Input 1 and Input 2 values) */}
          <Box sx={{ display: 'flex', justifyContent: 'center', my: -0.5 }}>
            <Tooltip title={t ? t('swapCurrencies') : 'Swap Currencies'}>
              <IconButton
                onClick={handleSwap}
                sx={{
                  backgroundColor: isDark ? 'rgba(59, 130, 246, 0.15)' : '#EFF6FF',
                  border: '1px solid',
                  borderColor: isDark ? 'rgba(59, 130, 246, 0.3)' : '#BFDBFE',
                  color: theme.palette.primary.main,
                  transform: `rotate(${rotation}deg)`,
                  transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.2s',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(59, 130, 246, 0.3)' : '#DBEAFE',
                  },
                }}
              >
                <CompareArrowsRoundedIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Input 2: To Currency Selector */}
          <TextField
            select
            label={t ? t('toCurrency') : 'To Currency'}
            value={toShort}
            onChange={(e) => setToShort(e.target.value)}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': { borderRadius: 2 },
            }}
          >
            {allCurrencyOptions.map((item, idx) => (
              <MenuItem key={idx} value={item.short}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <FlagIcon short={item.short} flag={item.flag} size={22} />
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {item.full || item.short}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ marginInlineStart: 'auto' }}>
                    {getDisplayShortCode(item.short)}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>

          {/* Result Card */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2.5,
              backgroundColor: isDark ? 'rgba(30, 41, 59, 0.7)' : '#F1F5F9',
              border: '1px solid',
              borderColor: isDark ? 'rgba(255, 255, 255, 0.08)' : '#E2E8F0',
              textAlign: 'center',
              mt: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
              {t ? t('converter') : 'Result'}
            </Typography>
            <Typography
              variant="h4"
              className="mono-num"
              sx={{ fontWeight: 800, color: theme.palette.primary.main, wordBreak: 'break-all' }}
            >
              {theme.direction === 'rtl' ? toPersianDigits(convertedTotal) : convertedTotal} <Typography component="span" variant="h6" sx={{ fontWeight: 700, ml: 0.5 }}>{getDisplayShortCode(toShort)}</Typography>
            </Typography>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              {theme.direction === 'rtl' ? '۱' : '1'} {getDisplayShortCode(fromShort)} = {theme.direction === 'rtl' ? toPersianDigits(unitRate >= 1000 ? unitRate.toLocaleString(undefined, { maximumFractionDigits: 2 }) : unitRate.toLocaleString(undefined, { maximumFractionDigits: 4 })) : (unitRate >= 1000 ? unitRate.toLocaleString(undefined, { maximumFractionDigits: 2 }) : unitRate.toLocaleString(undefined, { maximumFractionDigits: 4 }))} {getDisplayShortCode(toShort)}
            </Typography>
          </Paper>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ConverterModal;
